import * as React from "react";
import {
  injectIntl,
  WrappedComponentProps,
  FormattedMessage,
} from "react-intl";
import ThreadItem from "../threadItem";
import {
  Container,
  ReplyContainer,
  CollapseButton,
  ReplyInputWrapper,
} from "./styledComponents";
import PulseLoadingDot from "common/components/pulseLoadingDot";
import { px2rem } from "common/helpers/rem";
import { CountIndicatorBox } from "../styledComponents";
import ThreadInput from "../threadInput";
import Icon, { PACKS } from "common/icons";

interface IProps extends WrappedComponentProps {
  threadId: number | string;
  visible: boolean;
  targetReplyAuthor: Vingle.User.ISimplifiedUser | null;
  threads?: ReadonlyArray<Vingle.IThread>;
  threadsCount: number;
  paging: Vingle.IPaging;
  isLoading?: boolean;
  onWriteReply: (thread: Vingle.IThread) => void;
  onToggleView: () => void;
  onLoadMoreData: (pagingDirection: keyof Vingle.IPaging) => void;
  onAddThread: (
    content: string,
    media?: Vingle.MediaUpload.IUploadInput<Vingle.MediaUpload.MediaUploadData>,
    linkData?: Vingle.LinkUploadData | null,
  ) => void;
  onLike: (thread: Vingle.IThread) => void;
  handleCheckUser: () => boolean;
  handleHighlightUser?: (comment: Vingle.IThread) => boolean;
  disableLink?: boolean;
  disableUpload?: boolean;
}

const ThreadReply = React.memo(
  ({
    intl,
    threadId,
    visible,
    threads,
    threadsCount,
    paging,
    targetReplyAuthor,
    isLoading,
    onAddThread,
    onToggleView,
    onLike,
    onLoadMoreData,
    handleCheckUser,
    onWriteReply,
    handleHighlightUser,
    disableLink,
    disableUpload,
  }: IProps) => {
    const [
      threadTargetUser,
      setThreadTargetUser,
    ] = React.useState<Vingle.User.ISimplifiedUser | null>(null);
    const handleThreadTargetUser = React.useCallback(
      (thread: Vingle.IThread) => {
        if (handleCheckUser()) {
          setThreadTargetUser(thread.user);
          onWriteReply(thread);
        }
      },
      [handleCheckUser],
    );
    const handleLoadMoreBefore = React.useCallback(() => {
      onLoadMoreData("before");
    }, [onLoadMoreData]);
    const handleLoadMoreAfter = React.useCallback(() => {
      onLoadMoreData("after");
    }, [onLoadMoreData]);

    React.useEffect(() => {
      if (visible && targetReplyAuthor) {
        setThreadTargetUser(targetReplyAuthor);
      }
    }, [targetReplyAuthor]);
    if (!visible) {
      return null;
    } else if (isLoading && (!threads || !threads.length)) {
      return (
        <Container style={{ height: px2rem(100) }}>
          <PulseLoadingDot />
        </Container>
      );
    } else if (threads) {
      return (
        <Container>
          {Boolean(
            (paging.before || targetReplyAuthor || isLoading) &&
              threadsCount - threads.length,
          ) &&
            (isLoading ? (
              <div style={{ height: px2rem(50) }}>
                <PulseLoadingDot />
              </div>
            ) : (
              <CountIndicatorBox onClick={handleLoadMoreBefore}>
                <FormattedMessage
                  id="view_previous_thread"
                  values={{
                    contentType: intl.formatMessage({ id: "reply" }),
                  }}
                />
              </CountIndicatorBox>
            ))}
          <ReplyContainer>
            {threads.map(thread => (
              <ThreadItem
                key={`replyComment__${thread.id}`}
                parentId={threadId}
                thread={thread}
                onLike={onLike}
                isReply={true}
                onReplyWrite={handleThreadTargetUser}
                handleHighlightUser={handleHighlightUser}
              />
            ))}
          </ReplyContainer>
          {Boolean(paging.after && threadsCount - threads.length) &&
            (isLoading ? (
              <div style={{ height: px2rem(50) }}>
                <PulseLoadingDot />
              </div>
            ) : (
              <CountIndicatorBox onClick={handleLoadMoreAfter}>
                <FormattedMessage
                  id="view_more_thread"
                  values={{
                    contentType: intl.formatMessage({ id: "reply" }),
                  }}
                />
              </CountIndicatorBox>
            ))}
          <ReplyInputWrapper>
            <ThreadInput
              id={threadId}
              onAddThread={onAddThread}
              onInteraction={handleCheckUser}
              placeholder={intl.formatMessage({ id: "add_a_reply" })}
              inputId={`replyCommentInput${threadId}`}
              disableUpload={disableUpload}
              disableLink={disableLink}
              targetUser={threadTargetUser}
            />
          </ReplyInputWrapper>
          <CollapseButton onClick={onToggleView}>
            <Icon icon={PACKS.REPLY.COLLAPSE} width={29} height={18} />
          </CollapseButton>
        </Container>
      );
    }
    return null;
  },
);

export default injectIntl(ThreadReply);
